import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Utensils, TreePine } from 'lucide-react';
import { Recommendation, Category, CATEGORY_CONFIG } from './data';
interface CardCarouselProps {
  items: Recommendation[];
  activeCategory: Category | 'ALL';
  onCategoryChange: (cat: Category | 'ALL') => void;
  selectedId: number | null;
  onSelect: (id: number) => void;
}
const categories: (Category | 'ALL')[] = ['ALL', 'EATS', 'WALKS', 'AREAS'];
const DoodleEats = ({
  className
}: {
  className?: string;
}) => <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" data-id="element-14">
    <path d="M4 2.5c.2 0 .1 3.2.3 5 .2 1.5 1.2 2.8 2.8 2.8s2.5-1.2 2.7-2.8c.2-1.8.1-5 .3-5" data-id="element-15" />
    <path d="M7.1 10.3c-.1 2 0 5.5.1 7.2.1 1.5-.1 4.2-.1 4.2" data-id="element-16" />
    <path d="M16.5 2.2c-.3.5-1.2 2-1.5 4-.4 2.5.2 5 1.8 6l.7.3" data-id="element-17" />
    <path d="M17.5 12.5c.1 1.8-.2 5-.1 7 .1 1.5 0 2.3 0 2.3" data-id="element-18" />
    <path d="M17.8 2.3c-.1 2.5-.2 5.5.2 7.5.2 1.2.8 2.2.8 2.2" data-id="element-19" />
  </svg>;
const DoodleTree = ({
  className
}: {
  className?: string;
}) => <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" data-id="element-20">
    <path d="M12 22c.2 0 .1-3 0-5-.1-1.5.1-2 .1-2" data-id="element-21" />
    <path d="M7.5 15.5c-1.5-.8-3-2.5-2.5-4.2.4-1.5 2-1.8 2-1.8s-.8-1.5-.3-3c.5-1.3 2-1.8 2-1.8S9.2 3 10.5 2.2c1.2-.7 2.8-.2 3.5.5.8-.3 2-.2 2.8.5.7.7.8 1.8.5 2.5.8.5 1.5 1.5 1.5 2.8 0 1.5-1 2.5-1 2.5s1.2.8 1.2 2.5c0 1.8-1.5 3-3 3.5" data-id="element-22" />
    <path d="M8 15c.5.3 1.5.8 4 .8s3.5-.5 4-.8" data-id="element-23" />
  </svg>;
const DoodlePin = ({
  className
}: {
  className?: string;
}) => <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" data-id="element-24">
    <path d="M12 21.5c-.5-.5-7-6.5-7-11.8C5 5.5 8 2.2 12 2.2s7 3.3 7 7.5c0 5.3-6.5 11.3-7 11.8Z" data-id="element-25" />
    <circle cx="12" cy="9.5" r="2.5" data-id="element-26" />
    <path d="M10.2 8.8c.3-.5 1-.8 1.8-.8" data-id="element-27" />
  </svg>;
const CategoryIcon = ({
  category,
  className
}: {
  category: Category;
  className?: string;
}) => {
  switch (category) {
    case 'EATS':
      return <DoodleEats className={className} data-id="element-28" />;
    case 'WALKS':
      return <DoodleTree className={className} data-id="element-29" />;
    case 'AREAS':
      return <DoodlePin className={className} data-id="element-30" />;
  }
};
export function CardCarousel({
  items,
  activeCategory,
  onCategoryChange,
  selectedId,
  onSelect
}: CardCarouselProps) {
  const carouselRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<{
    [key: number]: HTMLDivElement | null;
  }>({});
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(true);
  const updateFades = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    setShowLeftFade(el.scrollLeft > 10);
    setShowRightFade(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    updateFades();
    el.addEventListener('scroll', updateFades, {
      passive: true
    });
    return () => el.removeEventListener('scroll', updateFades);
  }, [updateFades, items]);
  // Scroll to selected card when selectedId changes
  useEffect(() => {
    if (selectedId && itemRefs.current[selectedId] && carouselRef.current) {
      const el = itemRefs.current[selectedId];
      const container = carouselRef.current;
      const scrollLeft = el.offsetLeft - container.offsetLeft - container.clientWidth / 2 + el.clientWidth / 2;
      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }, [selectedId]);
  return <div className="flex flex-col bg-white pt-4 pb-8" data-id="element-31">
      {/* Tabs */}
      <div className="flex space-x-6 mb-4 overflow-x-auto no-scrollbar shrink-0" data-id="element-32">
        {categories.map(cat => <button key={cat} onClick={() => onCategoryChange(cat)} className={`pb-2 text-xs font-medium tracking-[0.2em] uppercase transition-colors relative whitespace-nowrap ${activeCategory === cat ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`} style={{
        fontFamily: "'DM Sans', system-ui, sans-serif"
      }} data-id="element-33">
            {cat}
            {activeCategory === cat && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-[2px] bg-gray-900 rounded-full" data-id="element-34" />}
          </button>)}
      </div>

      {/* Cards */}
      <div className="relative flex-1" data-id="element-35">
        {/* Left fade */}
        <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none transition-opacity duration-300" style={{
        opacity: showLeftFade ? 1 : 0
      }} data-id="element-36" />
        {/* Right fade */}
        <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none transition-opacity duration-300" style={{
        opacity: showRightFade ? 1 : 0
      }} data-id="element-37" />
        <div ref={carouselRef} className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar gap-4 pb-4 items-stretch" data-id="element-38">
          {items.length === 0 ? <div className="flex-1 flex items-center justify-center text-gray-400 text-sm italic" data-id="element-39">
              No recommendations found.
            </div> : items.map(item => {
          const isSelected = selectedId === item.id;
          const config = CATEGORY_CONFIG[item.category];
          return <motion.div layout initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} key={item.id} ref={el => itemRefs.current[item.id] = el} onClick={() => onSelect(item.id)} className={`
                  snap-center shrink-0 w-[240px] md:w-[270px] rounded-2xl border p-3.5 cursor-pointer 
                  transition-all duration-300 flex flex-col
                  ${isSelected ? 'bg-gray-50 border-gray-300 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.15)] -translate-y-2' : 'bg-white border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200'}
                `} data-id="element-40">
                  <div className="flex items-start justify-between mb-2 gap-2" data-id="element-41">
                    <h3 className="text-sm font-semibold text-gray-900 leading-tight" style={{
                fontFamily: "'DM Sans', system-ui, sans-serif"
              }} data-id="element-42">
                      {item.name}
                    </h3>
                    <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{
                backgroundColor: config.bg,
                color: config.color
              }} data-id="element-43">
                      <CategoryIcon category={item.category} className="w-4 h-4" data-id="element-44" />
                    </div>
                  </div>

                  <p className="text-gray-500 text-xs leading-relaxed mb-3 flex-1 italic" data-id="element-45">
                    {item.description}
                  </p>

                  <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100" data-id="element-46">
                    <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded-md" style={{
                backgroundColor: config.bg,
                color: config.color
              }} data-id="element-47">
                      {config.label}
                    </span>
                    <span className="text-[11px] font-medium text-gray-400 tracking-wide" data-id="element-48">
                      {item.neighborhood}
                    </span>
                  </div>
                </motion.div>;
        })}
        </div>
      </div>
    </div>;
}